/**
 * What a table copied out of a spreadsheet or a web page looks like as markdown.
 *
 * Pasting one is common enough in chat to be worth handling: Excel, Sheets and any table on a
 * page all put `text/html` on the clipboard alongside the plain text, and the plain text alone
 * is a wall of tab-separated values. Returns null for anything that is not a table, which is
 * the signal to leave the paste alone.
 */
export function htmlTableToMarkdown(html: string): string | null {
  if (typeof DOMParser === 'undefined') {
    return null;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const table = doc.querySelector('table');
  if (!table) {
    return null;
  }

  const allRows = Array.from(table.querySelectorAll('tr'));
  if (allRows.length === 0) {
    return null;
  }

  const thead = table.querySelector('thead');
  const tbody = table.querySelector('tbody');

  let headerCells: string[];
  let dataRows: Element[];
  if (thead) {
    const headerRow = thead.querySelector('tr');
    headerCells = headerRow ? cellsOf(headerRow) : [];
    dataRows = Array.from((tbody ?? table).querySelectorAll('tr'));
  } else {
    headerCells = cellsOf(allRows[0]);
    dataRows = allRows.slice(1);
  }

  if (headerCells.length === 0) {
    return null;
  }

  return [
    asRow(headerCells),
    asRow(headerCells.map(() => '---')),
    ...dataRows.map(row => asRow(cellsOf(row))),
  ].join('\n');
}

/** The images on the clipboard, if any — a screenshot pasted into a message is one of these. */
export function clipboardImageFiles(data: DataTransfer | null | undefined): File[] {
  const files = data?.files;
  if (!files?.length) {
    return [];
  }

  const images = Array.from(files).filter(file => file.type.startsWith('image'));
  return images.length === files.length ? Array.from(files) : images;
}

function cellsOf(row: Element): string[] {
  return Array.from(row.querySelectorAll('th, td')).map(cell =>
    (cell.textContent ?? '').trim().replace(/\|/g, '\\|'),
  );
}

function asRow(cells: string[]): string {
  return `| ${cells.join(' | ')} |`;
}
